import React, {useState, useCallback, useEffect} from "react";
import { 
  CreateTable,
  FailMessage,
  Loading,
  FormatValueToDecimal
} from "../common/APICommon";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../utils/Configuration";
import axios from 'axios';
// MUI Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArticleIcon from '@mui/icons-material/Article';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

export default function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [failMessage, setFailMessage] = useState(""); 
  const getCurrency = JSON.parse(localStorage.getItem("currency") || "{}");
  
//#region Table data
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);

  const headers = [
    'ACCOUNT', 
    'DESCRIPTION', 
    'DETAILS', 
    'TYPE', 
    'REFERENCE #', 
    'DEBIT', 
    'CREDIT', 
  ]
  const accessors = [
    'account', 
    'description', 
    'details', 
    'referenceTypeDescription', 
    'referenceNumber', 
    'debit', 
    'credit', 
  ]

  const actions = [
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/Report/dashboard` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
      fetchData();
  }, [fetchData])
//#endregion



  const formatValue = (val) => {
    const symbol = getCurrency?.symbol || "";
    return `${symbol}${FormatValueToDecimal(val || 0)}`;
  };

  const stats = [
    { 
      label: "Total Receivables", 
      amount: formatValue(data?.totalReceivables), 
      icon: <TrendingUpIcon sx={{ fontSize: '1.5rem' }} />, 
      bgColor: "bg-emerald-50", 
      iconColor: "text-emerald-600",
      borderColor: "border-emerald-100",
      textColor: "text-green-500",
    },
    { 
      label: "Total Payables", 
      amount: formatValue(data?.totalPayables), 
      icon: <TrendingDownIcon sx={{ fontSize: '1.5rem' }} />, 
      bgColor: "bg-rose-50", 
      iconColor: "text-rose-600",
      borderColor: "border-rose-100",
      textColor: `${data?.totalPayables > 0 ? "text-red-500" : "text-gray-600"}`,
    },
    { 
      label: "Cash Balance", 
      amount: `${data?.cash < 0 ? "-" : ""}${formatValue(Math.abs(data?.cash))}`, 
      icon: <AccountBalanceWalletIcon sx={{ fontSize: '1.5rem' }} />, 
      bgColor: "bg-sky-50", 
      iconColor: "text-sky-600",
      borderColor: "border-sky-100",
      textColor: `${data?.cash < 0 ? "text-red-500" 
        : data?.cash > 0 ? "text-green-500" 
        : "text-gray-600"}`,
    },
    { 
      label: "Pending Journals", 
      amount: data?.pendingJournals || 0, 
      icon: <AssessmentIcon sx={{ fontSize: '1.5rem' }} />, 
      bgColor: "bg-amber-50", 
      iconColor: "text-amber-600",
      borderColor: "border-amber-100",
      textColor: "text-gray-600",
    },
  ];

  const navigate = useNavigate();
  const newJournalEntryHandleClick = () => {
    navigate("/journals");
  }

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <DashboardIcon fontSize="large" className="text-theme-color" />
              Dashboard
            </h2>
            <p className="text-gray-500 mt-1">Real-time accounting overview and ledger activities.</p>
          </div>
          <div className="flex gap-3">
            <button onClick={()=>setFailMessage("This feature is under maintenance")} className="hidden items-center gap-2 bg-white border border-gray-300 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 transition shadow-sm">
              <ArticleIcon fontSize="small" /> Reports
            </button>
            <button onClick={newJournalEntryHandleClick} className="flex items-center gap-2 bg-theme-color text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-theme-color-h transition shadow-md">
              <AddCircleOutlineIcon fontSize="small" /> New Journal Entry
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className="relative overflow-hidden bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 group"
            >
              {/* Decorative Background Accent */}
              <div className={`absolute top-0 right-0 w-24 h-24 -mr-8 -mt-8 rounded-full opacity-20 transition-transform group-hover:scale-110 ${stat.bgColor}`} />

              <div className="relative flex flex-col h-full">
                {/* Icon Header */}
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${stat.bgColor} ${stat.iconColor} ring-4 ring-white shadow-sm`}>
                  {stat.icon}
                </div>

                {/* Content */}
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    {stat.label}
                  </p>
                  <div className="flex items-baseline">
                    <h3 className={`text-2xl font-extrabold ${stat.textColor} tracking-tight`}>
                      {stat.amount}
                    </h3>
                  </div>
                </div>

                {/* Bottom Accent Line */}
                {/* <div className={`absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500 ${stat.bgColor.replace('50', '500')}`} /> */}
              </div>
            </div>
          ))}
        </div>

        <h3 className="text-theme-color font-bold py-2">Recent Transactions</h3>

        <CreateTable
          tableName={"GL Account Transaction"}
          headers={headers}
          accessors={accessors}
          data={data?.glAccountTransactions ?? []}
          actions={actions}

          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={10}
          totalItems={data?.glAccountTransactions?.length}
        />
      </div>
      
      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
}